import React, { useEffect, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Modal } from './Modal';
import { ModalHeader } from './ModalHeader';
import { ModalDownloadButton } from './ModalDownloadButton';
import { ModalFilters } from './ModalFilters';
import SmallLoader from '../Loader/SmallLoader';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { useClassInsights } from './ClassInsightsContext';
import { SECTIONS } from './export/fileNaming';
import {
  catalogClasses,
  selectedClasses,
  selectedSubjects,
  selectedTaskTypes,
  deriveSubjectOptions,
  deriveTaskOptions,
  deriveStudentOptions,
} from './catalogSelectors';

const generateColor = (index) => {
  const colors = [
    '#1ade54', '#9f6bfa', '#fa6be7', '#6bfaee', '#faee6b',
    '#406bda', '#f1955c', '#ff6b9d', '#00bcd4', '#8bc34a'
  ];
  return colors[index % colors.length];
};

const formatOneDecimal = (value) => `${Number(value || 0).toFixed(1)}%`;
const BASE_CHART_OPTIONS = {
  responsive: true,
  plugins: {
    tooltip: {
      enabled: true,
      titleFont: {
        family: 'IBM Plex Sans',
      },
      bodyFont: {
        family: 'IBM Plex Sans',
      },
      callbacks: {
        title: (items) => items?.[0]?.label ?? '',
        label: () => null,
      },
    },
    legend: {
      display: false,
      labels: {
        font: {
          family: 'IBM Plex Sans',
        },
      },
    },
  },
  maintainAspectRatio: false,
};

export function CommentBanksModal({
  open,
  onClose,
  onExited,
  commentBanksList,
  selectedCommentBankIds,
  onCommentBankChange,
  commentBanksStats,
  isLoading,
  exportMode = false,
}) {
  const { filters, setFilters, catalog } = useClassInsights();
  const [openCommentKey, setOpenCommentKey] = useState(null);
  const [isSmallDevice, setIsSmallDevice] = useState(false);
  const rowRefs = React.useRef({});

  const stats = Array.isArray(commentBanksStats) ? commentBanksStats : [];
  const selectedIds = (selectedCommentBankIds || []).map((id) => String(id)).filter(Boolean);
  const selectedSet = new Set(selectedIds);
  const scopedStats = exportMode
    ? stats
    : selectedIds.length === 0
      ? stats
      : stats.filter((bank) => {
          const bankId = String(bank?.id ?? bank?.markingToolId ?? bank?._id ?? '');
          return selectedSet.has(bankId);
        });


  const suggestionMap = new Map();
  scopedStats.forEach((bank) => {
    (bank?.suggestions || []).forEach((suggestion) => {
      const title = String(suggestion.title || '').trim();
      if (!title) return;

      const usage = Number(suggestion.usagePercentage) || 0;
      const comments = (suggestion?.comments || [])
        .map((comment) => ({
          text: String(comment?.comment || '').trim(),
          percentage: Number(comment?.usagePercentage || 0),
        }))
        .filter((comment) => comment.text);

      const existing = suggestionMap.get(title);
      if (existing) {
        existing.percentage += usage;
        const existingCommentTexts = new Set(existing.comments.map(c => c.text));
        comments.forEach(comment => {
          if (!existingCommentTexts.has(comment.text)) {
            existing.comments.push(comment);
          }
        });
      } else {
        suggestionMap.set(title, {
          title,
          percentage: usage,
          comments,
        });
      }
    });
  });


  const listItems = exportMode
    ? scopedStats.flatMap((bank, bankIndex) => {
        const bankTitle = String(bank?.title || bank?.name || `Comment Bank ${bankIndex + 1}`).trim();
        return (bank?.suggestions || [])
          .map((suggestion) => {
            const suggestionTitle = String(suggestion?.title || '').trim();
            if (!suggestionTitle) return null;
            return {
              key: `${bankTitle}::${suggestionTitle}`,
              name: suggestionTitle,
              bankTitle,
              percentage: Number(Number(suggestion?.usagePercentage || 0).toFixed(1)),
              comments: (suggestion?.comments || [])
                .map((comment) => ({
                  text: String(comment?.comment || '').trim(),
                  percentage: Number(comment?.usagePercentage || 0),
                }))
                .filter((comment) => comment.text),
            };
          })
          .filter(Boolean);
      }).map((item, index) => ({
        ...item,
        color: generateColor(index),
      }))
    : Array.from(suggestionMap.values())
        .map((suggestion) => ({
          key: suggestion.title,
          name: suggestion.title,
          percentage: Number(suggestion.percentage.toFixed(1)),
          comments: suggestion.comments,
        }))
        .filter((item) => Number.isFinite(item.percentage) && item.percentage > 0)
        .sort((a, b) => b.percentage - a.percentage)
        .map((item, index) => ({
          ...item,
          color: generateColor(index),
        }));


  const pieItems = listItems.map((item) => ({
    key: item.key,
    name: item.name,
    percentage: item.percentage,
    color: item.color,
  }));

  const hasPieData = pieItems.length > 0;
  const chart = {
    labels: hasPieData ? pieItems.map((item) => item.name) : ['No data'],
    datasets: [
      {
        backgroundColor: hasPieData ? pieItems.map((item) => item.color) : ['#E5E0EB'],
        data: hasPieData ? pieItems.map((item) => item.percentage) : [100],
      },
    ],
  };

  const modalChartOptions = {
    ...BASE_CHART_OPTIONS,
    onClick: (_event, elements) => {
      if (!elements?.length) return;
      const clickedIndex = elements[0].index;
      const clickedSuggestionName = pieItems[clickedIndex]?.name;
      if (!clickedSuggestionName) return;
      setOpenCommentKey((prev) => prev === clickedSuggestionName ? null : clickedSuggestionName);
      requestAnimationFrame(() => {
        rowRefs.current[clickedSuggestionName]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    plugins: {
      ...BASE_CHART_OPTIONS.plugins,
      tooltip: {
        ...BASE_CHART_OPTIONS.plugins.tooltip,
        titleFont: {
          family: 'IBM Plex Sans',
        },
        bodyFont: {
          family: 'IBM Plex Sans',
        },
        callbacks: {
          title: (tooltipItems) => tooltipItems?.[0]?.label ?? '',
          label: (context) => `${context.formattedValue}%`,
        },
      },
      legend: {
        ...BASE_CHART_OPTIONS.plugins.legend,
        labels: {
          font: {
            family: 'IBM Plex Sans',
          },
        },
      },
    },
  };

  const csv = [];
  scopedStats.forEach((bank, bankIndex) => {
    const bankTitle = String(bank?.title || bank?.name || `Comment Bank ${bankIndex + 1}`).trim();
    if (scopedStats.length > 1) {
      csv.push({
        'Feedback Area': `Comment Bank: ${bankTitle}`,
        'Feedback Area %': '',
        Suggestion: '',
        'Suggestion %': '',
      });
    }

    (bank?.suggestions || []).forEach((suggestion) => {
      const suggestionTitle = String(suggestion?.title || '').trim();
      if (!suggestionTitle) return;

      const suggestionComments = Array.isArray(suggestion?.comments) ? suggestion.comments : [];
      if (suggestionComments.length === 0) {
        csv.push({
          'Feedback Area': suggestionTitle,
          'Feedback Area %': `${Number(suggestion?.usagePercentage || 0).toFixed(1)}%`,
          Suggestion: '-',
          'Suggestion %': '',
        });
      }

      suggestionComments.forEach((comment, commentIndex) => {
        const commentText = String(comment?.comment || '').trim();
        if (!commentText) return;

        csv.push({
          'Feedback Area': commentIndex === 0 ? suggestionTitle : '',
          'Feedback Area %': commentIndex === 0 ? `${Number(suggestion?.usagePercentage || 0).toFixed(1)}%` : '',
          Suggestion: commentText,
          'Suggestion %': `${Number(comment?.usagePercentage || 0).toFixed(1)}%`,
        });
      });
    });
  });

  // Build filter data for export
  const classes = catalogClasses(catalog);
  const classLabels = filters.classIds
    .map(id => classes.find(c => String(c.id) === String(id)))
    .filter(Boolean)
    .map(c => c.title || c.name);

  const commentBankLabels = selectedCommentBankIds
    .map(id => commentBanksList.find(cb => String(cb.id) === String(id)))
    .filter(Boolean)
    .map(cb => cb.title || cb.name || 'Untitled Comment Bank');

  const selectedClassesData = selectedClasses(catalog, filters);
  const selectedSubjectsData = selectedSubjects(selectedClassesData, filters);
  const selectedTaskTypesData = selectedTaskTypes(selectedSubjectsData, filters);

  const subjectOptions = deriveSubjectOptions(selectedClassesData);
  const taskOptions = deriveTaskOptions(selectedTaskTypesData);
  const studentOptions = deriveStudentOptions(selectedClassesData);

  const subjectLabels = filters.subjectCodes
    ?.map(code => subjectOptions.find(s => s.id === code)?.label)
    .filter(Boolean) || [];

  const taskTypeLabels = filters.taskTypes || [];

  const taskNameLabels = filters.taskIds
    ?.map(id => taskOptions.find(t => String(t.id) === String(id))?.label)
    .filter(Boolean) || [];

  const studentLabels = filters.studentIds
    ?.map(id => studentOptions.find(s => String(s.id) === String(id))?.label)
    .filter(Boolean) || [];

  const dateRange = filters.startDate && filters.endDate
    ? `${filters.startDate} to ${filters.endDate}`
    : null;

  const exportFilters = {
    classLabels,
    commentBankLabels,
    dateRange,
    filters: {
      subjects: subjectLabels,
      taskTypes: taskTypeLabels,
      taskIds: taskNameLabels,
      studentIds: studentLabels,
    },
  };

  return (
    <Modal
      open={open}
      onExited={onExited}
      onOverlayClick={onClose}
      className="ci-comment-bank-modal"
      data-section="comment-banks-modal"
    >
      <ModalHeader title="Specific Comments" onClose={onClose}>
        <ExportFormatDropdown
          section={SECTIONS.COMMENT_BANKS}
          data={commentBanksStats}
          csvData={csv}
          isModal={true}
          filterData={exportFilters}
          trigger={<ModalDownloadButton />}
        />
      </ModalHeader>

      <ModalFilters
        firstFilterLabel="Specific Comments"
        firstFilterOptions={commentBanksList}
        firstFilterValue={selectedCommentBankIds}
        firstFilterOnChange={(values) => {
          const normalizedValues = Array.isArray(values)
            ? values.filter((value) => value != null).map(String)
            : [];
          onCommentBankChange(normalizedValues);
        }}
        firstFilterGetOptionLabel={(opt) => opt.title || opt.name}
        firstFilterGetOptionValue={(opt) => String(opt.id)}
        filters={filters}
        setFilters={setFilters}
        firstFilterShowAllOption={false}
        firstFilterSingleSelect
        firstFilterShowCheckboxes
      />

      <div className={`ci-modal-content ci-comment-bank-modal-content ${isLoading ? 'is-loading' : ''}`}>
        {isLoading ? (
          <div className="ci-panel-loading ci-comment-bank-loading">
            <SmallLoader />
          </div>
        ) : (
          <>
            <div className="ci-modal-list">
              {listItems.length === 0 ? (
                <div className="ci-empty-state">No data available</div>
              ) : listItems.map((item, index) => {
                const showCommentsExpand = (item.comments || []).length > 0;
                const isCommentsExpanded = exportMode || openCommentKey === item.key;
                const showBankTitle =
                  exportMode &&
                  item.bankTitle &&
                  (index === 0 || listItems[index - 1]?.bankTitle !== item.bankTitle);

                return (
                  <div
                    key={item.key}
                    ref={(el) => {
                      rowRefs.current[item.name] = el;
                    }}
                    className="ci-modal-item-wrapper"
                  >
                    {showBankTitle && (
                      <div className="ci-modal-bank-title">{item.bankTitle}</div>
                    )}
                    <div className="ci-modal-item">
                      <div className="ci-modal-item-left">
                        <span className="ci-comment-dot" style={{ backgroundColor: item.color, color: item.color }} />
                        <span className="ci-modal-item-name">{item.name}</span>
                      </div>
                      <div className="ci-modal-item-right ci-comment-bank-item-right">
                        <span className="ci-modal-item-percentage">
                          {formatOneDecimal(item.percentage)}
                        </span>
                        {showCommentsExpand && !exportMode && (
                          <button
                            type="button"
                            className={`ci-comment-bank-row-toggle ${isCommentsExpanded ? 'open' : ''}`}
                            onClick={() => {
                              setOpenCommentKey((prev) => (prev === item.key ? null : item.key));
                              requestAnimationFrame(() => {
                                rowRefs.current[item.name]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                              });
                            }}
                            aria-label={`${isCommentsExpanded ? 'Collapse' : 'Expand'} ${item.name}`}
                          >
                            <svg className="ci-comment-bank-chevron" viewBox="0 0 20 20" aria-hidden>
                              <path
                                d="M5 7.5l5 5 5-5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    {showCommentsExpand && isCommentsExpanded && (
                      <div className="ci-modal-children ci-comment-bank-expand-children">
                        <div className="ci-comment-bank-expand-group">
                          {item.comments.map((comment, commentIndex) => (
                            <div
                              key={`${item.key}-comment-${commentIndex}`}
                              className="ci-modal-child-item ci-comment-bank-expand-comment-row"
                            >
                              <span className="ci-modal-child-name">{comment.text}</span>
                              <span className="ci-modal-child-percentage">
                                {formatOneDecimal(comment.percentage)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="ci-modal-chart">
              <Pie
                key={`comment-banks-modal-pie-${pieItems.length}-${chart.datasets[0].data.join('-')}`}
                data={chart}
                options={modalChartOptions}
                height={isSmallDevice ? 220 : 300}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
